import { useEffect, useState } from 'react';

export const getDifference = (endTime: string): number => {
    const countDownDate = new Date(endTime).getTime();
    const now = new Date().getTime();
    return countDownDate - now;
};

export const getTimeDifferenceInUnits = (endTime: string, disableDays = false): [number, number, number, number] => {
    let days = 0;
    let hours = 0;
    let minutes = 0;
    let seconds = 0;

    const distance = getDifference(endTime);

    if (distance <= 0) {
        return [days, hours, minutes, seconds];
    }

    days = Math.floor(distance / (1000 * 60 * 60 * 24));
    hours = Math.floor((distance % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));

    // If the days are disabled, add them to hours
    if (disableDays) {
        hours = days * 24 + hours;
    }
    minutes = Math.floor((distance % (1000 * 60 * 60)) / (1000 * 60));
    seconds = Math.floor((distance % (1000 * 60)) / 1000);

    return [days, hours, minutes, seconds];
};

export default function useTimer(
    endTime: string = new Date().toISOString(),
    disableDays = false,
): [number, number, number, number, boolean] {
    const initDistance = getDifference(endTime);
    const [initDays, initHours, initMinutes, initSeconds] = getTimeDifferenceInUnits(endTime, disableDays);

    const [days, setDays] = useState<number>(initDays);
    const [hours, setHours] = useState<number>(initHours);
    const [minutes, setMinutes] = useState<number>(initMinutes);
    const [seconds, setSeconds] = useState<number>(initSeconds);
    const [done, setDone] = useState<boolean>(initDistance <= 0);

    useEffect(() => {
        let interval = 0;
        const distance = getDifference(endTime);
        if (distance > 0) {
            const timer = (timerInterval: number): void => {
                const [newDays, newHours, newMinutes, newSeconds] = getTimeDifferenceInUnits(endTime, disableDays);
                setDays(newDays);
                setHours(newHours);
                setMinutes(newMinutes);
                setSeconds(newSeconds);

                const newDistance = getDifference(endTime);
                const isDone = newDistance <= 0;
                setDone(isDone);

                if (isDone) {
                    clearInterval(timerInterval);
                }
            };
            interval = setInterval(timer, 1000);
        }

        return (): void => clearInterval(interval);
    }, [endTime, disableDays]);

    return [days, hours, minutes, seconds, done];
}
